import React from 'react'

import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom'

import { lazyWithVersionCheck } from '@libs/typescript-utils'

import { authActions } from '@services/index'

import {
  getDefaultRedirectUrlWithCompany,
  getPulseRedirectPreferredForCompanyType,
  getRouteUrl,
  isAdmin,
  userHasEditorPermission,
} from '@helpers'

import { CompanyConsentProvider } from '@contexts/CompanyConsentProvider/CompanyConsentProvider'

import { isOnboardingBlockedForCompany } from '@hooks'

import { CenteredPageError, CenteredPageLoader, PageLoadingErrorView } from '@components/ui'

import { RouteKeys, ROUTES, TmsUserPagePermissions } from '@constants'

import { permissionDeniedForUser } from '@permissions'

import { AuthorizedContent } from '../AuthorizedContent'
import { MaintenanceView } from '../MaintenanceView'

// code splitting - async component import
const AsyncDashboardCostPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardCostPage'),
  PageLoadingErrorView
)
const AsyncDashboardPartnersPage = lazyWithVersionCheck(
  () => import('@components/pages/DashboardPartnersPage'),
  PageLoadingErrorView
)
const AsyncDashboardPartnerDetailsPage = lazyWithVersionCheck(
  () => import('@components/pages/DashboardPartnerDetailsPage'),
  PageLoadingErrorView
)
const AsyncNotFoundPrivatePage = lazyWithVersionCheck(
  () => import('@components/pages/NotFoundPage/NotFoundPrivatePage'),
  PageLoadingErrorView
)
const AsyncDashboardCompanyPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardCompanyPage'),
  PageLoadingErrorView
)
const AsyncDashboardIncomePage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardIncomePage'),
  PageLoadingErrorView
)
const AsyncDashboardQuarantineInvoicePage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardQuarantineInvoicePage'),
  PageLoadingErrorView
)
const AsyncSubscriptionLandingPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/SubscriptionLandingPage'),
  PageLoadingErrorView
)
const AsyncPulsePage = lazyWithVersionCheck(() => import('@components/pages/PulsePage'), PageLoadingErrorView)
const AsyncAutotaxOnboardingPage = lazyWithVersionCheck(
  () => import('@components/pages/AutotaxOnboardingPage'),
  PageLoadingErrorView
)
const AsyncTransactionsPage = lazyWithVersionCheck(
  () => import('@components/pages/TransactionsPage'),
  PageLoadingErrorView
)
const AsyncDocumentsMultiUploadPage = lazyWithVersionCheck(
  () => import('@oldComponents/pages/DashboardDocumentsMultiUploadPage'),
  PageLoadingErrorView
)
const AsyncDokumentDetailsPage = lazyWithVersionCheck(
  () => import('@components/pages/DashboardDokumentDetailsPage'),
  PageLoadingErrorView
)
const AsyncCashflowPage = lazyWithVersionCheck(() => import('@components/pages/CashflowPage'), PageLoadingErrorView)
const AsyncTaxationDetailsPage = lazyWithVersionCheck(
  () => import('@components/pages/TaxationDetailsPage'),
  PageLoadingErrorView
)
const AsyncCompanyDocumentsPage = lazyWithVersionCheck(
  () => import('@components/pages/CompanyDocumentsPage'),
  PageLoadingErrorView
)

interface DashboardCompanyRoutesProps {
  availableCompanies: CompaniesItem[]
  callSelectCompany: (id: number) => void
  company: Nullable<Company>
  companyDetailsError: Nullable<string>
  hasSubscription: boolean
  hasTaxNumber: boolean
  isAdminUser: boolean
  isCashflowVisibleForUser: boolean
  isCompanyFetched?: boolean
  isCompanyLoading: boolean
  isDokumentsVisibleForUser: boolean
  isEditorUser: boolean
  isExpenseVisibleForUser: boolean
  isIncomeVisibleForUser: boolean
  isPartnersVisibleForUser: boolean
  isQuarantineVisibleForUser: boolean
  isTaxVisibleForUser: boolean
  lastRequestedCompanyId: Nullable<Company['id']>
  tabOrder: TransactionsDataTypeProps[]
}

function PureDashboardCompanyRoutes({
  availableCompanies,
  callSelectCompany,
  company,
  companyDetailsError,
  hasSubscription,
  isCashflowVisibleForUser,
  isCompanyFetched,
  isCompanyLoading,
  isDokumentsVisibleForUser,
  isEditorUser,
  isExpenseVisibleForUser,
  isIncomeVisibleForUser,
  isPartnersVisibleForUser,
  isQuarantineVisibleForUser,
  isTaxVisibleForUser,
  lastRequestedCompanyId,
  tabOrder,
}: DashboardCompanyRoutesProps) {
  const location = useLocation()
  const { company_id: companyIdParameter } = useParams<{
    company_id?: string
  }>()
  const companyIdFromUrl = Number(companyIdParameter) || null
  const companyFromUrl = availableCompanies.find(item => item.id === companyIdFromUrl)
  const isValidCompany = Boolean(companyFromUrl)
  const isCompanySynced =
    companyFromUrl?.id === company?.id || (companyDetailsError && companyFromUrl?.id === lastRequestedCompanyId)
  const companyTypeToCheck =
    companyIdFromUrl && companyIdFromUrl === company?.id ? company.company_type : (companyFromUrl?.company_type ?? null)
  const isPulseRedirectPreferred = getPulseRedirectPreferredForCompanyType(companyTypeToCheck)
  const isOnboardingBlocked = isOnboardingBlockedForCompany(companyFromUrl)

  // Sync store with url and trigger company details fetch for company
  React.useEffect(() => {
    if (isValidCompany && companyIdFromUrl != null && !isCompanyLoading && !isCompanySynced) {
      callSelectCompany(companyIdFromUrl)
    }
  }, [callSelectCompany, companyIdFromUrl, isCompanyLoading, isCompanySynced, isValidCompany])

  if (companyDetailsError) {
    return (
      <MaintenanceView>
        <CenteredPageError
          title={
            <FormattedMessage
              id="screen.fetchCompanyDetails.failure.title"
              defaultMessage="Sajnáljuk! A cégadatok betöltése sikertelen."
            />
          }
          errorMessage={companyDetailsError}
        />
      </MaintenanceView>
    )
  }

  if (isValidCompany) {
    if ((!isCompanyFetched || isCompanyLoading) && !isCompanySynced) {
      // syncing company in store with url - w/o this route will change to no-subscription
      // because "hasSubscription" comes from selected company
      return (
        <CenteredPageLoader>
          <FormattedMessage
            id="screen.fetchCompanyDetails.loading"
            defaultMessage="Cégadatok betöltése folyamatban..."
          />
        </CenteredPageLoader>
      )
    }

    if (isOnboardingBlocked) {
      const onboardingUrl = getRouteUrl(RouteKeys.ONBOARDING, companyIdFromUrl)

      return (
        <MaintenanceView>
          <CompanyConsentProvider>
            <Routes key={company?.id}>
              <Route path="onboarding" element={<AsyncAutotaxOnboardingPage />} />
              <Route
                path="company"
                element={
                  hasSubscription ? (
                    <AuthorizedContent accessLevel="admin">
                      <AsyncDashboardCompanyPage />
                    </AuthorizedContent>
                  ) : (
                    <Navigate to={onboardingUrl} replace />
                  )
                }
              />
              {/* pulse included: until the accountant approves the company there are no reports to show */}
              <Route path="*" element={<Navigate to={onboardingUrl} replace />} />
            </Routes>
          </CompanyConsentProvider>
        </MaintenanceView>
      )
    }

    // need to add store value as key to switch to update company specific info on url change
    // TODO find better solution: trigger async-fetch when params.companyId changes
    //! NOTE: companyStore is required so the network requests' order is important!

    // where to go?
    // TODO move it to context?
    let defaultRedirectUrl = getRouteUrl(RouteKeys.NO_SUBSCRIPTION, companyIdFromUrl)
    if (hasSubscription) {
      defaultRedirectUrl = getDefaultRedirectUrlWithCompany(companyIdFromUrl as number, {
        isCashflowVisibleForUser,
        isDokumentsVisibleForUser,
        isExpenseVisibleForUser,
        isIncomeVisibleForUser,
        isPartnersVisibleForUser,
        isPulseRedirectPreferred,
        isQuarantineVisibleForUser,
        isTaxVisibleForUser,
        tabOrder,
      })
    }

    return (
      <MaintenanceView>
        <CompanyConsentProvider>
          <Routes key={company?.id}>
            <Route
              path="no-subscription"
              element={!hasSubscription ? <AsyncSubscriptionLandingPage /> : <Navigate to={ROUTES.root} replace />}
            />
            {/* these routes only available with active subscription */}
            <Route
              path="income/:invoice_id?"
              element={
                hasSubscription && isIncomeVisibleForUser ? (
                  <AsyncDashboardIncomePage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              key={location.key} // need to reload details route after successful file upload
              path="expense/:invoice_id?"
              element={
                hasSubscription && isExpenseVisibleForUser ? (
                  <AsyncDashboardCostPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              path="quarantine/:invoice_id?"
              element={
                hasSubscription && isQuarantineVisibleForUser ? (
                  <AsyncDashboardQuarantineInvoicePage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              element={
                // TODO tabOrder does not include dokuments yet
                hasSubscription ? (
                  <AsyncTransactionsPage defaultRedirectUrl={defaultRedirectUrl} />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
              path="transactions/*"
            />
            <Route
              element={
                hasSubscription && isPartnersVisibleForUser ? (
                  <AsyncDashboardPartnersPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
              path="partners/*"
            />
            <Route
              element={
                hasSubscription && isPartnersVisibleForUser ? (
                  <AsyncDashboardPartnerDetailsPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
              path="partner/:partner_id"
            />
            <Route
              element={
                hasSubscription && isCashflowVisibleForUser ? (
                  <AsyncCashflowPage defaultRedirectUrl={defaultRedirectUrl} />
                ) : (
                  <Navigate to={defaultRedirectUrl} />
                )
              }
              path="cash-flow/*"
            />
            <Route
              path="pulse"
              element={hasSubscription ? <AsyncPulsePage /> : <Navigate to={defaultRedirectUrl} replace />}
            />
            <Route
              path="onboarding"
              element={<Navigate to={getRouteUrl(RouteKeys.PULSE, companyIdFromUrl)} replace />}
            />
            <Route
              path="taxation-details"
              element={
                hasSubscription && companyFromUrl?.service_type === 'autotax' ? (
                  <AsyncTaxationDetailsPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              path="documents-multi-upload"
              element={
                hasSubscription && isEditorUser && isDokumentsVisibleForUser ? (
                  <AsyncDocumentsMultiUploadPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              path="document/:document_id"
              element={
                hasSubscription && isDokumentsVisibleForUser ? (
                  <AsyncDokumentDetailsPage />
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            <Route
              path="documents"
              element={hasSubscription ? <AsyncCompanyDocumentsPage /> : <Navigate to={defaultRedirectUrl} replace />}
            />
            <Route
              path="company"
              element={
                hasSubscription ? (
                  <AuthorizedContent accessLevel="admin">
                    <AsyncDashboardCompanyPage />
                  </AuthorizedContent>
                ) : (
                  <Navigate to={defaultRedirectUrl} replace />
                )
              }
            />
            {/* redirect to an existing route*/}
            <Route path="/" element={<Navigate to={defaultRedirectUrl} replace />} />
            <Route path="*" element={<AsyncNotFoundPrivatePage />} />
          </Routes>
        </CompanyConsentProvider>
      </MaintenanceView>
    )
  } else {
    return <AsyncNotFoundPrivatePage />
  }
}

const DashboardCompanyRoutes = connect(
  (state: Store) => ({
    availableCompanies: state.auth.companies,
    companyDetailsError: state.auth.company.error,
    hasTaxNumber: Boolean(state.auth.company.data?.tax_account_number),
    isAdminUser: isAdmin(state.auth.company.data?.role ?? null),
    isCashflowVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyCashflowPermission),
    isCompanyFetched: state.auth.company.fetched,
    isCompanyLoading: state.auth.company.loading,
    isDokumentsVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyDocumentPermission),
    isEditorUser: userHasEditorPermission(state.auth.company.data?.role), // company can be empty
    isExpenseVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyExpensePermission),
    isIncomeVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyIncomePermission),
    isPartnersVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyPartnerPermission),
    isQuarantineVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyNavPermission),
    isTaxVisibleForUser: !permissionDeniedForUser(state, TmsUserPagePermissions.denyTaxPermission),
    lastRequestedCompanyId: state.auth.company.lastRequestedCompanyId,
    tabOrder: state.auth.user.preferences.tab_order,
  }),
  { callSelectCompany: authActions.selectCompany.request }
)(PureDashboardCompanyRoutes)

DashboardCompanyRoutes.displayName = 'DashboardCompanyRoutes'

export default DashboardCompanyRoutes
